import { useEffect, useState, useCallback } from "react";
import { type PlayerRow, getLuckPermsStyle, extractPAPI, extractPlayerName } from "../page";
export const SERVER_ID = import.meta.env.VITE_SERVER_ID 
  || "02ac8ccc-475c-48c4-929d-a416aa8a3fab";
const STATS_API = "https://api.applesmp.us";

/**
 * Like extractPAPI but returns the raw string value instead of a number.
 */
const extractPAPIString = (data: Record<string, unknown>, key: string): string => {
  const extensions = data?.extensions;
  if (!Array.isArray(extensions)) return "";

  const variants = [key, `%${key}%`];

  for (const ext of extensions as unknown[]) {
    const extData = (ext as Record<string, unknown>)?.extensionData;
    if (!Array.isArray(extData)) continue;
    for (const section of extData as unknown[]) {
      const tabs = (section as Record<string, unknown>)?.tabs;
      if (!Array.isArray(tabs)) continue;
      for (const tab of tabs as unknown[]) {
        const values = (tab as Record<string, unknown>)?.values;
        if (!Array.isArray(values)) continue;
        for (const entry of values as unknown[]) {
          const ve = entry as Record<string, unknown>;
          const desc = ve?.description as Record<string, unknown> | undefined;
          const name = String(desc?.name ?? ve?.name ?? "");
          if (variants.some((v) => name === v || name.includes(key))) {
            // value is a plain string — just return it directly
            return String(ve?.value ?? "").replace(/<[^>]+>/g, "").trim();
          }
        }
      }
    }
  }
  return "";
};

import { formatMoney, formatCompact } from "../../../utils/format";

interface PlaytimeEntry {
  label: string;
  hours: number;
}

interface PlayerProfileModalProps {
  player: PlayerRow | null;
  onClose: () => void;
}

interface DetailedPlayer extends PlayerRow {
  fetchedDetail?: boolean;
  nicestMoney?: string;
}

// ─────────────────────────────────────────────────────────────────────────────

const planStr = (val: unknown): string | undefined => {
  if (val === null || val === undefined) return undefined;
  if (typeof val === "object" && "v" in (val as object)) {
    const v = (val as { v: unknown }).v;
    return v != null ? String(v).replace(/<[^>]+>/g, "").trim() : undefined;
  }
  return typeof val === "string" ? val.replace(/<[^>]+>/g, "").trim() : String(val);
};

const planDate = (val: unknown): string | undefined => {
  const raw = planStr(val);
  if (!raw) return undefined;
  if (/^\d{10,}$/.test(raw)) {
    const ms = raw.length >= 13 ? Number(raw) : Number(raw) * 1000;
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  }
  return raw;
};

// ─────────────────────────────────────────────────────────────────────────────

export default function PlayerProfileModal({
  player,
  onClose,
}: PlayerProfileModalProps) {
  const [detailed, setDetailed] = useState<DetailedPlayer | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((username: string) => {
    navigator.clipboard.writeText(username).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      const el = document.createElement("textarea");
      el.value = username;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const fetchDetail = useCallback(async (username: string) => {
    setLoadingDetail(true);
    try {
      // Fetch MongoDB stats and Plan detail in parallel
      const [mongoRes, planRes] = await Promise.all([
        fetch(`${STATS_API}/stats/${encodeURIComponent(username)}`, { signal: AbortSignal.timeout(6000) }),
        fetch(`https://status.applesmp.us/v1/player?player=${encodeURIComponent(username)}&server=${SERVER_ID}`, { signal: AbortSignal.timeout(6000) }),
      ]);

      // MongoDB stats (money, kills, deaths, playtime)
      let money = 0, kills = 0, deaths = 0, mongoPlaytimeMs = 0, mongoGroup = "";
      if (mongoRes.ok) {
        const mongo = await mongoRes.json() as { money?: number; kills?: number; deaths?: number; playtime?: number; group?: string };
        money           = mongo.money    ?? 0;
        kills           = mongo.kills    ?? 0;
        deaths          = mongo.deaths   ?? 0;
        mongoPlaytimeMs = mongo.playtime ?? 0;
        mongoGroup      = mongo.group    ?? "";
      }

      // Plan detail (online status, dates, luckperms)
      let online = false;
      let planPlaytimeMs = 0;
      let joinDate: string | undefined;
      let lastSeen: string | undefined;

      if (planRes.ok) {
        const data = await planRes.json() as Record<string, unknown>;
        const info = (data?.info ?? {}) as Record<string, unknown>;
        online         = typeof info.online === "boolean" ? info.online : false;
        planPlaytimeMs = typeof info.active_playtime === "number" ? info.active_playtime : 0;
        joinDate       = planDate(info.registered ?? data?.registered);
        lastSeen       = planDate(info.last_seen  ?? data?.last_seen);
      }

      // Always prefer MongoDB playtime (total economy playtime) over Plan's active_playtime
      // which only counts recent non-AFK sessions and will differ from the leaderboard value
      const playtimeMs = mongoPlaytimeMs > 0 ? mongoPlaytimeMs : planPlaytimeMs;
      const formatPlaytime = (ms: number) => ms > 0 ? `${Math.round(ms / 3_600_000)}h` : "0h";

      setDetailed((prev) => ({
        ...(prev ?? ({} as DetailedPlayer)),
        username,
        score:          money,
        kills,
        deaths,
        nicestMoney:    money > 0 ? formatMoney(money) : (prev?.nicestMoney ?? "$0"),
        playtime:       formatPlaytime(playtimeMs),
        playtimeMs,
        online,
        luckpermsGroup: mongoGroup || prev?.luckpermsGroup || "default",
        luckpermsColor: prev?.luckpermsColor ?? null,
        rank:           prev?.rank           ?? 0,
        badge:          prev?.badge          ?? null,
        joinDate:       joinDate ?? prev?.joinDate,
        lastSeen:       lastSeen ?? prev?.lastSeen,
        fetchedDetail:  true,
      }));
    } catch {
      // silently fall back to prop data
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (!player) return;
    setDetailed({ ...player, fetchedDetail: false });
    setCopied(false);
    fetchDetail(player.username);
  }, [player, fetchDetail]);

  useEffect(() => {
    if (!player) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [player, onClose]);

  if (!player || !detailed) return null;

  const rankStyle = getLuckPermsStyle(detailed.luckpermsGroup, detailed.luckpermsColor);
  const pos = rankPosition(detailed.rank);
  const kd =
    detailed.deaths > 0
      ? (detailed.kills / detailed.deaths).toFixed(2)
      : detailed.kills.toFixed(2);

  const isOwner = isOwnerGroup(detailed.luckpermsGroup);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-card overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "modalIn 0.18s ease-out" }}
      >
        {/* Header band */}
        <div
          className={`relative border-b border-border/50 px-6 pt-6 pb-4 ${
            isOwner
              ? "bg-gradient-to-br from-amber-500/10 via-yellow-400/5 to-transparent"
              : "bg-primary/8"
          }`}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>

          {loadingDetail && (
            <div className="absolute top-3 left-3 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              <span className="text-[10px] text-muted-foreground/60">Fetching live data...</span>
            </div>
          )}

          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <img
                src={`https://mc-heads.net/avatar/${detailed.username}/72`}
                alt={detailed.username}
                className={`h-[72px] w-[72px] rounded-xl object-cover bg-primary/10 border ${
                  isOwner ? "border-amber-400/40" : "border-border/60"
                }`}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    "https://mc-heads.net/avatar/MHF_Steve/72";
                }}
              />
              {isOwner && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center justify-center">
                  <i className="ri-vip-crown-fill text-amber-400 text-lg drop-shadow-[0_0_6px_rgba(251,191,36,0.7)]" />
                </div>
              )}
              <div className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-card border border-border/60">
                <i className={`${pos.icon} text-xs ${pos.color}`} />
              </div>
              <span
                className={`absolute top-1 right-1 h-3 w-3 rounded-full border-2 border-card ${
                  detailed.online ? "bg-green-500" : "bg-gray-500/60"
                }`}
                title={detailed.online ? "Currently online" : "Offline"}
              />
            </div>

            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-xl leading-none flex items-center gap-1.5">
                  {detailed.username}
                  {isOwner && (
                    <i className="ri-vip-crown-2-fill text-amber-400 text-sm" title="Server Owner" />
                  )}
                </h2>
                <span
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded-full leading-none ${
                    rankStyle.hexColor
                      ? ""
                      : `${rankStyle.textClass} ${rankStyle.bgClass}`
                  } ${isOwner ? "ring-1 ring-amber-400/30" : ""}`}
                  style={
                    rankStyle.hexColor
                      ? { color: rankStyle.hexColor, backgroundColor: `${rankStyle.hexColor}22` }
                      : undefined
                  }
                >
                  {isOwner && <i className="ri-vip-crown-fill mr-1 text-[10px]" />}
                  {rankStyle.label}
                </span>
              </div>
              <p className={`text-sm mt-1 font-medium ${pos.color}`}>{pos.label}</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span
                  className={`flex items-center gap-1 text-xs font-medium ${
                    detailed.online ? "text-green-500" : "text-muted-foreground"
                  }`}
                >
                  <i className={detailed.online ? "ri-wifi-line" : "ri-wifi-off-line"} />
                  {detailed.online ? "Online now" : "Offline"}
                </span>
                {detailed.joinDate && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <i className="ri-calendar-line text-xs" />
                    Joined {detailed.joinDate}
                  </span>
                )}
                {detailed.lastSeen && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <i className="ri-time-line text-xs" />
                    {detailed.lastSeen}
                  </span>
                )}
              </div>

              <button
                onClick={() => handleCopy(detailed.username)}
                className={`mt-2.5 flex items-center gap-1.5 self-start px-2.5 py-1 rounded-md text-xs font-medium border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  copied
                    ? "border-green-500/40 bg-green-500/10 text-green-400"
                    : "border-border/60 bg-background/60 text-muted-foreground hover:text-foreground hover:border-border hover:bg-accent"
                }`}
              >
                <i className={`text-xs ${copied ? "ri-check-line" : "ri-clipboard-line"}`} />
                {copied ? "Copied!" : "Copy username"}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="px-6 pt-5 pb-4">
          <div className="grid grid-cols-3 gap-2 mb-5">
            <StatCard
              icon="ri-copper-coin-line"
              label="Balance"
              value={detailed.nicestMoney || formatMoney(detailed.score)}
              fullValue={`$${detailed.score.toLocaleString()}`}
              color="text-green-400"
            />
            <StatCard icon="ri-sword-line" label="Kills" value={formatCompact(detailed.kills)} color="text-red-400" />
            <StatCard icon="ri-skull-line" label="Deaths" value={formatCompact(detailed.deaths)} color="text-orange-400" />
            <StatCard icon="ri-bar-chart-line" label="K/D Ratio" value={kd} highlight />
            <StatCard icon="ri-time-line" label="Playtime" value={detailed.playtime} color="text-yellow-400" />
            <StatCard icon="ri-trophy-line" label="Rank #" value={`#${detailed.rank}`} />
          </div>

          {detailed.playtimeHistory && detailed.playtimeHistory.length > 0 && (
            <div className="rounded-lg bg-background/60 border border-border/40 px-4 py-3">
              <PlaytimeGraph data={detailed.playtimeHistory} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between">
          {detailed.fetchedDetail ? (
            <span className="flex items-center gap-1.5 text-xs text-green-500/70">
              <i className="ri-checkbox-circle-line text-xs" />
              Live stats
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/40">Loading live stats...</span>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
      `}</style>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({
  icon,
  label,
  value,
  fullValue,
  highlight,
  color,
}: {
  icon: string;
  label: string;
  value: string;
  fullValue?: string;
  highlight?: boolean;
  color?: string;
}) => (
  <div className="relative group rounded-lg bg-background/60 border border-border/40 px-4 py-3 flex flex-col gap-1">
    <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
      <i className={`${icon} text-xs`} />
      <span>{label}</span>
    </div>
    <span
      className={`text-base font-bold leading-none ${
        color ?? (highlight ? "text-primary" : "text-foreground")
      }`}
    >
      {value}
    </span>
    {fullValue && (
      <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-20 rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-mono text-foreground whitespace-nowrap shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-150">
        {fullValue}
      </div>
    )}
  </div>
);

const PlaytimeGraph = ({ data }: { data: PlaytimeEntry[] }) => {
  const max = Math.max(...data.map((d) => d.hours), 1);
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">
        Weekly Activity (last 8 weeks)
      </p>
      <div className="flex items-end gap-1.5 h-20">
        {data.map((entry, i) => {
          const heightPct = (entry.hours / max) * 100;
          const isLatest = i === data.length - 1;
          return (
            <div key={entry.label} className="flex flex-col items-center gap-1 flex-1 group">
              <div
                className={`w-full rounded-sm transition-all duration-300 ${
                  isLatest ? "bg-primary" : "bg-primary/30 group-hover:bg-primary/60"
                }`}
                style={{ height: `${Math.max(heightPct, 8)}%` }}
                title={`${entry.hours}h`}
              />
              <span className="text-[9px] text-muted-foreground/50 font-mono leading-none">
                {entry.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const rankPosition = (rank: number) => {
  if (rank === 1) return { icon: "ri-medal-line", color: "text-yellow-400", label: "1st Place" };
  if (rank === 2) return { icon: "ri-medal-line", color: "text-slate-300",  label: "2nd Place" };
  if (rank === 3) return { icon: "ri-medal-line", color: "text-amber-600",  label: "3rd Place" };
  return { icon: "ri-hashtag", color: "text-muted-foreground", label: `${rank}th Place` };
};

const isOwnerGroup = (group: string) =>
  group.toLowerCase() === "owner" || group.toLowerCase().includes("owner");
